"use client";

import { useId, useRef, useState } from "react";

export type MobileDesignerView = "chat" | "front" | "back" | "details";
const VIEWS: MobileDesignerView[] = ["chat", "front", "back", "details"];

export default function DesignerPanelNavigation({
  view,
  onSelect,
  detailsPanelId,
  detailsTriggerRef,
  status,
  statusView = "front",
  pendingCount = 0,
  unreadPreviews = { front: false, back: false },
}: {
  view: MobileDesignerView;
  onSelect: (view: MobileDesignerView) => void;
  detailsPanelId: string;
  detailsTriggerRef?: React.RefObject<HTMLButtonElement | null>;
  status?: string | null;
  statusView?: "front" | "back";
  pendingCount?: number;
  unreadPreviews?: { front: boolean; back: boolean };
}) {
  const statusId = useId();
  const pendingId = useId();
  const [focusChoice, setFocusChoice] = useState<{
    selected: MobileDesignerView;
    focused: MobileDesignerView;
  } | null>(null);
  const focusTab = focusChoice?.selected === view ? focusChoice.focused : view;
  const buttons = useRef<
    Partial<Record<MobileDesignerView, HTMLButtonElement | null>>
  >({});
  return (
    <div
      className="designer-mobile-navigation shrink-0 lg:hidden"
      data-slot="designer-mobile-navigation"
    >
      <div
        role="tablist"
        aria-label="Designer views"
        className="designer-mobile-tabs"
        onBlur={event => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null))
            setFocusChoice(null);
        }}
      >
        {VIEWS.map(tab => {
          const unread =
            (tab === "front" || tab === "back") && unreadPreviews[tab];
          return (
            <button
              key={tab}
              ref={element => {
                buttons.current[tab] = element;
                if (tab === "details" && detailsTriggerRef)
                  detailsTriggerRef.current = element;
              }}
              type="button"
              role="tab"
              id={`designer-mobile-tab-${tab}`}
              aria-label={tab === "details" ? "Design details" : undefined}
              aria-selected={view === tab}
              aria-controls={
                tab === "details" ? detailsPanelId : `designer-${tab}-panel`
              }
              aria-describedby={
                unread
                  ? `${statusId}-${tab}-unread`
                  : tab === "details" && pendingCount > 0
                    ? pendingId
                    : tab === statusView && status
                      ? statusId
                      : undefined
              }
              tabIndex={focusTab === tab ? 0 : -1}
              onFocus={() => setFocusChoice({ selected: view, focused: tab })}
              onClick={() => onSelect(tab)}
              onKeyDown={event => {
                const index = VIEWS.indexOf(tab);
                const next =
                  event.key === "ArrowRight"
                    ? VIEWS[(index + 1) % VIEWS.length]
                    : event.key === "ArrowLeft"
                      ? VIEWS[(index + VIEWS.length - 1) % VIEWS.length]
                      : event.key === "Home"
                        ? VIEWS[0]
                        : event.key === "End"
                          ? VIEWS[VIEWS.length - 1]
                          : null;
                if (!next) return;
                event.preventDefault();
                // Focus alone must not change the visible design or draft.
                buttons.current[next]?.focus();
              }}
              className="designer-tab relative flex h-11 min-w-0 items-center justify-center rounded-xl text-sm font-medium"
            >
              {tab === "details"
                ? "Details"
                : tab === "chat"
                  ? "Chat"
                  : tab === "front"
                    ? "Front"
                    : "Back"}
              {unread ? (
                <span
                  data-slot="preview-unread-indicator"
                  aria-hidden="true"
                  className="absolute right-1.5 top-1.5 size-1.5 rounded-full bg-brand-logo"
                />
              ) : null}
              {unread ? (
                <span
                  id={`${statusId}-${tab}-unread`}
                  className="sr-only"
                  aria-hidden="true"
                >
                  New {tab} preview
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      {status ? (
        <span
          id={statusId}
          role="status"
          aria-live="polite"
          className="sr-only"
        >
          {status}
        </span>
      ) : null}
      {pendingCount > 0 ? (
        <span id={pendingId} className="sr-only">
          {pendingCount} pending {pendingCount === 1 ? "change" : "changes"}
        </span>
      ) : null}
    </div>
  );
}
